import React, { useEffect, useId, useRef, useState } from 'react';
import TicketSelectOption from './TicketSelectOption';

const getLabel = option => [...new Set([option.name, option.code].filter(Boolean))].join(' / ');
const getCategoryDescription = option => `Category: ${option.category?.name || option.category?.code || '-'} · Subcategory: ${option.sub_category?.name || option.sub_category?.code || '-'}`;

const RecordReferenceSelect = ({ field, value, onChange, disabled }) => {
  const [search, setSearch] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const resultsRef = useRef(null);
  const options = field.options || [];
  const selected = options.find(option => option._id === value);
  const text = search ?? (selected ? getLabel(selected) : '');
  const matches = options.filter(option => [option.name, option.code].some(part => part?.toLowerCase().includes(text.trim().toLowerCase())));
  const id = useId();
  useEffect(() => {
    if (open) resultsRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);
  const select = option => {
    onChange(option._id);
    setSearch(null);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <label htmlFor={id}>{field.label}{field.required ? ' *' : ''}</label>
      <input id={id} className="input" role="combobox" autoComplete="off"
        aria-expanded={open} aria-controls={`${id}-results`} aria-autocomplete="list"
        aria-activedescendant={open && matches[activeIndex] ? `${id}-${activeIndex}` : undefined}
        placeholder={`Search ${field.label.toLowerCase()} by name or code...`}
        value={text} disabled={disabled}
        onFocus={() => { if (!selected) setOpen(true); }}
        onBlur={() => setOpen(false)}
        onChange={event => {
          setSearch(event.target.value);
          setOpen(true);
          setActiveIndex(0);
          if (value) onChange('');
        }}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex(index => open ? Math.max(0, Math.min(matches.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))) : 0);
          }
          if (event.key === 'Enter' && open) {
            event.preventDefault();
            if (matches[activeIndex]) select(matches[activeIndex]);
          }
        }} />
      {open && <div ref={resultsRef} id={`${id}-results`} role="listbox" aria-label={field.label}
        style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30, display: 'grid', gap: '0.25rem', maxHeight: '260px', overflowY: 'auto', padding: '0.35rem', border: '1px solid var(--border-light)', borderRadius: '8px', backgroundColor: 'var(--bg-card)', boxShadow: '0 12px 28px rgba(var(--neutral-rgb), 0.12)' }}>
        {matches.length ? matches.map((option, index) => <TicketSelectOption
          key={option._id} id={`${id}-${index}`} role="option" aria-selected={index === activeIndex}
          label={getLabel(option)} description={field.key === 'provider_id' && field.showCategory !== false ? getCategoryDescription(option) : undefined}
          style={index === activeIndex ? { backgroundColor: 'var(--bg-soft)' } : undefined}
          onMouseDown={event => event.preventDefault()} onClick={() => select(option)} disabled={disabled}
        />) : <div style={{ padding: '0.5rem 0.6rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>No results found.</div>}
      </div>}
    </div>
  );
};

export default RecordReferenceSelect;
